'use client'

import SubPageNav from '@/components/SubPageNav'
import GrandTitle from '@/components/GrandTitle'
import Mosaic from '@/components/Mosaic'
import { ou, contenuDe, mosaiqueTuiles } from '@/lib/contenuEditable'
import { phraseProchainePage, boutonProchainePage, type EtatSoiree } from '@/lib/composition/annonceSoiree'
import type { SectionConfig } from '@/types/site'

/**
 * Page « Notre Histoire » — mise en page PREMIUM dédiée (chronologie).
 *
 * Même langage que « Révélation des Étoiles » (hero encre marine +
 * constellation, DA champagne) mais articulé autour d'une FRISE verticale.
 * Textes pilotés par le CRM ; chaque valeur absente retombe sur son défaut.
 */

const INK = '#021734'
const CREAM = '#F7EFE1'
const IVORY = '#FBF6EC'
const ACCENT = '#C2A66B'

interface Etape { year?: string; title?: string; text?: string }
interface Bloc {
  eyebrow?: string
  titleAlt?: string
  tagline?: string
  title?: string
  subtitle?: string
  steps?: Etape[]
  tiles?: string[]
  primaryButton?: { label?: string; href?: string }
  secondaryButton?: { label?: string; href?: string }
}

function paragraphes(texte: string): string[] {
  return texte.split('\n\n').filter((p) => p.trim() !== '')
}

function Eyebrow({ children, onDark = false }: { children: React.ReactNode; onDark?: boolean }) {
  return (
    <div className="text-center">
      <div style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(11px, 0.6875rem + 0.15vw, 13px)', letterSpacing: '0.34em', textTransform: 'uppercase', fontWeight: 600, color: ACCENT }}>
        {children}
      </div>
      <div style={{ width: 44, height: 1, background: ACCENT, margin: '16px auto 0', opacity: onDark ? 0.9 : 0.85 }} aria-hidden />
    </div>
  )
}

function Constellation() {
  return (
    <svg aria-hidden viewBox="0 0 1200 700" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
      <g stroke={ACCENT} strokeWidth={0.6} opacity={0.26} fill="none">
        <line x1="160" y1="110" x2="300" y2="180" /><line x1="300" y1="180" x2="410" y2="110" />
        <line x1="880" y1="150" x2="1000" y2="240" /><line x1="1000" y1="240" x2="1080" y2="150" />
        <line x1="200" y1="560" x2="330" y2="500" /><line x1="860" y1="560" x2="980" y2="510" />
      </g>
      <g fill={ACCENT} opacity={0.5}>
        <circle cx="160" cy="110" r="1.8" /><circle cx="300" cy="180" r="2.3" /><circle cx="410" cy="110" r="1.6" />
        <circle cx="880" cy="150" r="1.6" /><circle cx="1000" cy="240" r="2.3" /><circle cx="1080" cy="150" r="1.6" />
        <circle cx="200" cy="560" r="1.8" /><circle cx="330" cy="500" r="2.1" /><circle cx="860" cy="560" r="1.8" /><circle cx="980" cy="510" r="2.1" />
      </g>
      <g fill="#ffffff" opacity={0.22}>
        <circle cx="600" cy="90" r="1" /><circle cx="90" cy="360" r="1" /><circle cx="1130" cy="400" r="1" /><circle cx="640" cy="620" r="1" />
      </g>
    </svg>
  )
}

export default function NotreHistoireContenu(
  { sections, etatSoiree }: { sections: SectionConfig[]; etatSoiree: EtatSoiree },
) {
  const couverture = contenuDe<Bloc>(sections, 'couverture')
  const heritage = contenuDe<Bloc>(sections, 'heritage')
  const chrono = contenuDe<Bloc>(sections, 'chronologie')
  const moments = contenuDe<Bloc>(sections, 'moments')
  const cta = contenuDe<Bloc>(sections, 'cta')

  const steps = chrono?.steps && chrono.steps.length > 0 ? chrono.steps : []
  // Galerie « Moments forts » — visuels du dossier cliente « Notre Histoire »,
  // orientation respectée (tuile portrait → image portrait, tuile large → paysage).
  // La 6e (paysage) ne sert qu'à combler la mosaïque mobile (masquée en desktop).
  // 6 emplacements à forme fixe + images par défaut ; chaque image est
  // remplaçable via l'Éditeur (section « moments » → tuile1…tuile6).
  const galerie = mosaiqueTuiles(moments as Record<string, unknown> | undefined, [
    { src: '/media/histoire/portrait-1.jpg', o: 'portrait' }, // 1 — grande tuile (portrait)
    { src: '/media/histoire/paysage-1.jpg', o: 'paysage' },   // 2 — large (paysage)
    { src: '/media/histoire/portrait-2.jpg', o: 'portrait' }, // 3 — portrait
    { src: '/media/histoire/portrait-3.jpg', o: 'portrait' }, // 4 — portrait
    { src: '/media/histoire/paysage-2.jpg', o: 'paysage' },   // 5 — large (paysage)
    { src: '/media/histoire/paysage-3.jpg', o: 'paysage' },   // 6 — paysage (mobile uniquement)
  ])

  return (
    <>
      <SubPageNav />

      <main style={{ backgroundColor: CREAM, color: INK, fontFamily: 'var(--font-body, inherit)' }}>
        {/* ─── HERO ─────────────────────────────────────────── */}
        <section
          className="relative overflow-hidden flex items-center justify-center text-center"
          style={{ minHeight: 'clamp(280px, 42vh, 460px)', color: '#fff', background: 'radial-gradient(ellipse 70% 66% at 50% 42%, #0a2247, #04173a 60%, #021734 100%)' }}
        >
          <Constellation />
          <div className="relative px-6" style={{ zIndex: 2 }}>
            <Eyebrow onDark>{ou(couverture?.eyebrow, 'Notre Histoire')}</Eyebrow>
            {/* Le H1 : le champ « Titre » du CRM (`title`, type hero) le pilote
                enfin ; `titleAlt` reste le repli — il portait le titre jusqu'ici,
                le rendu ne bouge pas tant que « Titre » n'est pas renseigné. */}
            <GrandTitle as="h1" dark style={{ margin: '22px 0 0', maxWidth: '92vw', textShadow: '0 2px 30px rgba(0,0,0,0.4)' }}>
              {ou(couverture?.title, ou(couverture?.titleAlt, "14 ans de soirées d'exception"))}
            </GrandTitle>
            {/* L'accroche : le champ « Sous-titre » du CRM (`subtitle`) la pilote ;
                `tagline` reste le repli qui portait le texte jusqu'ici. */}
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontStyle: 'italic', fontSize: 'clamp(19px, 2.4vw, 28px)', color: '#e9e0cd', marginTop: 18 }}>
              {ou(couverture?.subtitle, ou(couverture?.tagline, 'Une tradition qui se réinvente, saison après saison.'))}
            </p>
          </div>
        </section>

        {/* ─── L'HÉRITAGE ───────────────────────────────────── */}
        <section style={{ padding: 'clamp(78px, 9vw, 120px) 24px' }}>
          <div className="mx-auto text-center" style={{ maxWidth: 720 }}>
            <Eyebrow>{ou(heritage?.eyebrow, "L'héritage")}</Eyebrow>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(24px, 3vw, 36px)', lineHeight: 1.32, fontWeight: 400, marginTop: 28 }}>
              {ou(heritage?.title, 'Depuis plus de dix ans, nous réunissons celles et ceux qui font vivre la culture.')}
            </p>
            {ou(heritage?.subtitle, '') !== '' && (
              <p style={{ fontSize: 16, lineHeight: 1.85, color: 'rgba(22,36,58,0.78)', maxWidth: 620, margin: '18px auto 0', whiteSpace: 'pre-line' }}>{heritage?.subtitle}</p>
            )}
          </div>
        </section>

        {/* ─── LA CHRONOLOGIE (frise) ───────────────────────── */}
        <section style={{ backgroundColor: IVORY, padding: 'clamp(60px, 7vw, 90px) 24px clamp(80px, 10vw, 120px)' }}>
          <Eyebrow>{ou(chrono?.eyebrow, 'La chronologie')}</Eyebrow>
          <div className="nh-timeline" style={{ maxWidth: 940, margin: '52px auto 0', position: 'relative' }}>
            {steps.map((s, i) => (
              <div key={i} className={`nh-step ${i % 2 === 0 ? 'nh-odd' : 'nh-even'}`}>
                <span className="nh-dot" style={{ background: ACCENT, boxShadow: '0 0 0 5px rgba(194,166,107,0.18)' }} aria-hidden />
                <div style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 34, color: ACCENT, lineHeight: 1 }}>{s.year}</div>
                <h3 style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 24, fontWeight: 500, margin: '6px 0 8px' }}>{s.title}</h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.7, color: 'rgba(22,36,58,0.72)' }}>{s.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── MOMENTS FORTS (galerie) ──────────────────────── */}
        <section style={{ padding: 'clamp(78px, 9vw, 120px) 24px' }}>
          <div className="mx-auto text-center" style={{ maxWidth: 1080 }}>
            <Eyebrow>{ou(moments?.eyebrow, 'Moments forts')}</Eyebrow>
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(26px, 3.2vw, 40px)', lineHeight: 1.28, fontWeight: 400, maxWidth: 720, margin: '28px auto 0' }}>
              {ou(moments?.title, 'Quatorze années en images.')}
            </p>
            <Mosaic images={galerie} uid="nh" />
            <div style={{ marginTop: 'clamp(36px, 5vw, 56px)' }}>
              <a href="/galerie" className="gde-btn gde-btn--solid">Souvenirs — revoir toutes les images</a>
            </div>
          </div>
        </section>

        {/* ─── CTA FINAL ────────────────────────────────────── */}
        <section style={{ backgroundColor: INK, color: '#fff', padding: 'clamp(80px, 10vw, 128px) 24px', textAlign: 'center' }}>
          <div className="mx-auto" style={{ maxWidth: 720 }}>
            <div style={{ width: 44, height: 1, background: ACCENT, margin: '0 auto 26px' }} aria-hidden />
            <p style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(26px, 3.6vw, 44px)', lineHeight: 1.24, fontWeight: 400, marginBottom: 30 }}>
              {ou(cta?.title, phraseProchainePage(etatSoiree))}
            </p>
            <div className="nh-btns" style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              {/* Le CRM garde la main ; à défaut, le bouton suit la soirée —
                  « Réserver ma place » tant qu'elle est à venir, « Rester
                  informé » ensuite. */}
              <a
                href={ou(cta?.primaryButton?.href, boutonProchainePage(etatSoiree).href)}
                className="gde-btn gde-btn--solid"
              >
                {ou(cta?.primaryButton?.label, boutonProchainePage(etatSoiree).label)}
              </a>
              <a href={ou(cta?.secondaryButton?.href, '/')} className="gde-btn gde-btn--on-dark">
                {ou(cta?.secondaryButton?.label, 'Découvrir le Gala')}
              </a>
            </div>
          </div>
        </section>
      </main>

      <style>{`
        .nh-timeline::before {
          content: ""; position: absolute; left: 50%; top: 6px; bottom: 6px; width: 1px;
          background: linear-gradient(180deg, transparent, rgba(194,166,107,0.55) 8%, rgba(194,166,107,0.55) 92%, transparent);
          transform: translateX(-0.5px);
        }
        .nh-step { position: relative; width: 50%; padding: 0 46px 54px; box-sizing: border-box; }
        .nh-step.nh-odd { left: 0; text-align: right; }
        .nh-step.nh-even { left: 50%; text-align: left; }
        .nh-dot { position: absolute; top: 8px; width: 11px; height: 11px; border-radius: 50%; }
        .nh-step.nh-odd .nh-dot { right: -6px; }
        .nh-step.nh-even .nh-dot { left: -6px; }
        @media (max-width: 820px) {
          .nh-timeline::before { left: 7px; }
          .nh-step { width: 100% !important; left: 0 !important; text-align: left !important; padding: 0 0 46px 34px; }
          .nh-step .nh-dot { left: 2px !important; right: auto !important; }
          /* Boutons du CTA final sur une seule ligne, labels enroulés */
          .nh-btns { flex-wrap: nowrap !important; gap: 10px !important; }
          .nh-btns > a { flex: 1 1 0 !important; min-width: 0 !important; }
          .nh-btns .gde-btn { white-space: normal !important; padding: 11px 10px !important; font-size: 10.5px !important; letter-spacing: 0.08em !important; line-height: 1.35 !important; }
        }
      `}</style>
    </>
  )
}
